"use client"

import { ProductCard } from "@/components/organisms/ProductCard/ProductCard"
import LocalizedClientLink from "@/components/molecules/LocalizedLink/LocalizedLink"
import { HttpTypes } from "@medusajs/types"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useScreenSize } from "@/hooks/useScreenSize"

interface ModernNewArrivalsSectionProps {
  products?: (HttpTypes.StoreProduct & { seller?: any })[]
  locale?: string
  heading?: string
  subtitle?: string
  ctaText?: string
}

const OBSERVER_ROOT_MARGIN = "100px"
const OBSERVER_THRESHOLD = 0.01

export const ModernNewArrivalsSection = ({
  products = [],
  locale = process.env.NEXT_PUBLIC_DEFAULT_REGION || "pl",
  heading,
  subtitle,
  ctaText
}: ModernNewArrivalsSectionProps) => {
  const [visibleProducts, setVisibleProducts] = useState<Set<number>>(new Set())
  const productRefs = useRef<(HTMLDivElement | null)[]>([])
  const observerRef = useRef<Map<number, IntersectionObserver>>(new Map())

  // 根据屏幕宽度动态计算列数，保持两行商品
  const screenSize = useScreenSize()
  const colsPerRow = screenSize === "lg" ? 5 : screenSize === "md" ? 4 : 3
  const visibleProductsInitial = colsPerRow
  const maxProductsToDisplay = colsPerRow * 2

  // Initialize visible products with first row
  useEffect(() => {
    const initialVisible = new Set<number>()
    for (let i = 0; i < Math.min(visibleProductsInitial, products.length); i++) {
      initialVisible.add(i)
    }
    setVisibleProducts(initialVisible)
  }, [products.length, visibleProductsInitial])

  // Intersection Observer callback
  const handleIntersection = useCallback((index: number) =>
    (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setVisibleProducts((prev) => {
            const newSet = new Set(prev)
            newSet.add(index)
            return newSet
          })
          // Disconnect observer once product is visible
          const observer = observerRef.current.get(index)
          if (observer) {
            observer.disconnect()
            observerRef.current.delete(index)
          }
        }
      })
    },
    [])

  // Setup Intersection Observers
  useEffect(() => {
    const observers = observerRef.current

    productRefs.current.forEach((ref, index) => {
      // Skip if already visible or no ref
      if (visibleProducts.has(index) || !ref || index < visibleProductsInitial) {
        return
      }

      // Clean up existing observer for this index
      const existingObserver = observers.get(index)
      if (existingObserver) {
        existingObserver.disconnect()
        observers.delete(index)
      }

      // Create new observer
      const observer = new IntersectionObserver(handleIntersection(index), {
        rootMargin: OBSERVER_ROOT_MARGIN,
        threshold: OBSERVER_THRESHOLD,
      })

      observer.observe(ref)
      observers.set(index, observer)
    })

    return () => {
      // Cleanup all observers
      observers.forEach((observer) => observer.disconnect())
      observers.clear()
    }
  }, [products.length, handleIntersection, visibleProducts])

  // Memoized products to display (always two rows)
  const productsToDisplay = useMemo(() =>
    products.slice(0, maxProductsToDisplay),
    [products, maxProductsToDisplay]
  )

  // Handle empty products
  if (products.length === 0) {
    return null
  }

  return (
    <section
      className="relative w-full my-[60px]"
      aria-label="New Arrivals Section"
    >
      <div className="mx-auto w-full">
        {/* Section Header */}
        <div className="mb-8 lg:mb-10 2xl:mb-12 text-center">
          <h2 className="text-3xl font-semibold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
            {heading}
          </h2>
          <p className="mt-2 text-base text-muted-foreground lg:text-lg">
            {subtitle}
          </p>
        </div>

        {/* Products Grid */}
        <div className="flex justify-center">
          <div
            className="inline-grid grid-cols-3 min-[1025px]:grid-cols-4 min-[1441px]:grid-cols-5 gap-[30px] w-full"
            role="list"
            aria-label="New arrival products"
          >
            {productsToDisplay.map((product, index) => (
              <div
                key={product.id}
                ref={(el) => {
                  productRefs.current[index] = el
                }}
                className="group relative"
                role="listitem"
              >
                {visibleProducts.has(index) ? (
                  <div
                    className="relative overflow-hidden rounded-lg bg-secondary/5 transition-all duration-300 hover:bg-secondary/10 flex justify-center"
                    aria-label={`Product: ${product.title}`}
                  >
                    <ProductCard
                      product={product}
                      api_product={product}
                      locale={locale}
                      showAddToCartButton={false}
                      showVariantCount={true}
                      className="!max-w-none !min-w-0"
                    />
                  </div>
                ) : (
                  <div
                    className="aspect-[3/4] animate-pulse rounded-lg bg-gray-100"
                    aria-label="Loading product"
                    role="status"
                  />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* View All Link */}
        <div className="mt-12 text-center">
          <LocalizedClientLink
            href="/categories"
            className="inline-flex items-center justify-center gap-2 bg-black text-white text-[16px] rounded-lg w-[200px] py-[14px] transition-all hover:bg-gray-800"
            aria-label="View all new arrivals"
          >
            {ctaText}
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M14.4301 5.92969L20.5001 11.9997L14.4301 18.0697" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3.5 12H20.33" stroke="white" strokeWidth="1.5" strokeMiterlimit="10" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </LocalizedClientLink>
        </div>
      </div>
    </section>
  )
}
